import {
  browserSupportsWebAuthnAutofill,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

/**
 * Passkeys in the browser (ADR 0011): the server hands out the WebAuthn options and checks the
 * answer; between the two the browser asks the person for their fingerprint, face or device PIN.
 */

type Options = Record<string, unknown>;

/**
 * The salt for the PRF extension (ADR 0022): the same everywhere, since conditional UI doesn't know
 * the passkey before the browser picks one. Each passkey's output for it is its own secret, which
 * opens the person's key where garages are encrypted.
 */
const PRF_SALT = new TextEncoder().encode("bimmertools-inventory garage key v1");

/** The options with the PRF extension asked for; the browser wants the salt as bytes. */
function withPrf<T extends Options>(options: T): T {
  const extensions = (options.extensions ?? {}) as Options;
  return { ...options, extensions: { ...extensions, prf: { eval: { first: PRF_SALT } } } };
}

type Answer = { id: string; clientExtensionResults?: unknown };

/**
 * Takes the PRF output out of a passkey's answer, base64url, or null where the browser or the
 * passkey has none. It goes to our server beside the answer, not inside it (docs/learnings.md).
 */
export function takePrf(answer: Answer): string | null {
  const results = answer.clientExtensionResults as
    { prf?: { results?: { first?: ArrayBuffer | ArrayBufferView } } } | undefined;
  const first = results?.prf?.results?.first;
  if (results?.prf) delete results.prf;
  if (!first) return null;
  const bytes =
    first instanceof ArrayBuffer
      ? new Uint8Array(first)
      : new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
  return base64url(bytes);
}

function base64url(bytes: Uint8Array): string {
  let text = "";
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

/** A new passkey for the signed-in person; asks for PRF, which some give already here. */
export function createPasskey(options: Options) {
  return startRegistration({
    optionsJSON: withPrf(options) as unknown as PublicKeyCredentialCreationOptionsJSON,
  });
}

/**
 * The PRF output of a passkey just made, for browsers that give it only when it is used: one
 * more touch, with a challenge of our own, since nothing is signed in by it.
 */
export async function passkeyPrf(credentialId: string): Promise<string | null> {
  const credential = (await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [{ type: "public-key", id: fromBase64url(credentialId) }],
      userVerification: "required",
      extensions: { prf: { eval: { first: PRF_SALT } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;
  if (!credential) return null;
  return takePrf({
    id: credential.id,
    clientExtensionResults: credential.getClientExtensionResults(),
  });
}

function fromBase64url(text: string): Uint8Array<ArrayBuffer> {
  const plain = atob(text.replaceAll("-", "+").replaceAll("_", "/"));
  const bytes = new Uint8Array(plain.length);
  for (let i = 0; i < plain.length; i++) bytes[i] = plain.charCodeAt(i);
  return bytes;
}

/**
 * Signs in with a passkey. With `autofill` the browser offers its passkeys in the email field's
 * suggestions (the field's autocomplete ends in "webauthn") and waits until one is picked; a
 * request without it, from the passkey button, ends that wait.
 */
export function pickPasskey(options: Options, autofill = false) {
  return startAuthentication({
    optionsJSON: withPrf(options) as unknown as PublicKeyCredentialRequestOptionsJSON,
    useBrowserAutofill: autofill,
  });
}

export const passkeyAutofill = browserSupportsWebAuthnAutofill;

/** The person closed the browser's dialog, it timed out, or a newer request replaced it. */
export function passkeyCancelled(error: unknown): boolean {
  return (
    error instanceof Error && (error.name === "NotAllowedError" || error.name === "AbortError")
  );
}

/** "iPhone", "Mac" or "Windows" for a new passkey's name, from the browser; else null. */
export function deviceName(userAgent = navigator.userAgent): string | null {
  const devices: [RegExp, string][] = [
    [/iPhone/, "iPhone"],
    [/iPad/, "iPad"],
    [/Android/, "Android"],
    [/Macintosh|Mac OS X/, "Mac"],
    [/Windows/, "Windows"],
    [/Linux/, "Linux"],
  ];
  return devices.find(([pattern]) => pattern.test(userAgent))?.[1] ?? null;
}
